(ns kyber.lib.pdftron.core
  (:require
   [applied-science.js-interop :as j]
   [goog.object :as gobj]
   [clojure.core.async :refer [go go-loop <! >! chan]]
   [re-frame.core :as re-frame]

   [kyber.constants :refer [pdftron-api-key]]
   [kyber.utils :as ku]
   [kyber.lib.pdftron.constants :as c]
   [kyber.lib.pdftron.styles :refer [set-tool-styles!]]
   [kyber.lib.pdftron.toolbar :as toolbar]
   [kyber.lib.pdftron.get-visible-annotations
    :refer [get-visible-annotations]]
   [kyber.lib.pdftron.get-viewport-rect
    :refer [get-viewport-rect]]
   [kyber.lib.pdftron.hotkeys :as hotkeys]))

(def ^:private scroll-debounce-interval 300)

;; Public Helper Functions

(defonce pdf-viewer-instances (atom {}))

(defn get-pdftron-viewer
  [viewer-key]
  (get @pdf-viewer-instances viewer-key))

(defn get-tool
  [doc-viewer tool-name]
  (js-invoke doc-viewer "getTool" (name tool-name)))

(defn set-tool-mode
  [doc-viewer tool-name]
  (js-invoke doc-viewer "setToolMode" (get-tool doc-viewer tool-name)))

(defn get-annotation-id [annotation]
  (gobj/get annotation "Id"))

(defn get-current-page-index [{:keys [doc-viewer]}]
  (if (some? doc-viewer)
    (dec (js-invoke doc-viewer "getCurrentPage"))
    (js/console.error "doc-viewer getCurrentPage not available")))

;; END

(def ^:private PDFTronWebViewer (gobj/getValueByKeys js/window "WebViewer"))
(def ^:private poll-document-interval 1000)
(def ^:private invalid-shared-pdftron-element
  [;; Left Panel
   "notesPanelButton"
   "notesPanel"
   "outlinesPanelButton"
   "outlinesPanel"
   "thumbnailsPanelButton"
   "leftPanelTabs"
   "contextMenuPopup"

   ;; Annotation
   "textUnderlineToolButton"
   "textSquigglyToolButton"
   "textStrikeoutToolButton"
   "annotationStyleEditButton"
   "annotationContentOverlay"
   "annotationGroupButton"])

(def ^:private invalid-learner-pdftron-elements
  (into invalid-shared-pdftron-element
        []))

(def ^:private invalid-admin-pdftron-elements
  (into invalid-shared-pdftron-element
        ["annotationDeleteButton"]))

(def ^:private invalid-view-only-pdftron-elements
  (into invalid-shared-pdftron-element
        ["annotationDeleteButton"
         "linkButton"
         "textHighlightToolButton"]))

(defn- get-visible-pages
  [doc-viewer]
  (-> doc-viewer
      (js-invoke "getDisplayModeManager")
      (js-invoke "getDisplayMode")
      (js-invoke "getVisiblePages")))

(defn- annotations->xfdf
  "Converts a list of  annotation to and xfdf string"
  [annotation-manager annotations]
  (let [c (chan)]
    (-> annotation-manager
        (js-invoke "exportAnnotations" (clj->js {:annotList annotations}))
        (js-invoke "then" #(go (>! c %))))
    c))

(defn- annotation->xfdf
  "Converts an annotation to and xfdf string"
  [annotation-manager annotation]
  (annotations->xfdf annotation-manager [annotation]))

(defn- get-first-annotation
  "Annotation object does not implement cljs data-structures"
  [annotation-list]
  (gobj/get annotation-list 0))

(defn- dispatch-visible-annotations
  "Dispatches that an annotation is currently visible to the user.
  NOTE: This can be triggered from different events and should be
  thought of as an idempotent event."
  ([options apis]
   (let [viewport-rect (get-viewport-rect apis)
         visible-annotations (get-visible-annotations apis viewport-rect)]
     (dispatch-visible-annotations options apis visible-annotations)))
  ([options apis visible-annotations]
   (let [{:keys [event-bus
                 disable-visible-dispatch
                 disable-visibility-change-dispatch
                 disable-upper-fourth-dispatch
                 disable-upper-third-dispatch
                 disable-middle-third-dispatch
                 disable-lower-third-dispatch]
          :or {disable-visible-dispatch false
               disable-visibility-change-dispatch false
               disable-upper-fourth-dispatch false
               disable-upper-third-dispatch false
               disable-middle-third-dispatch false
               disable-lower-third-dispatch false}}
         options

         {:keys [on-annotation-visible
                 on-annotation-visibility-change
                 on-annotation-visible-upper-fourth
                 on-annotation-visible-upper-third
                 on-annotation-visible-middle-third
                 on-annotation-visible-lower-third
                 on-annotation-visible-lower-three-fourths]}
         event-bus

         {:keys [in-viewport
                 in-upper-fourth
                 in-upper-third
                 in-middle-third
                 in-lower-third
                 in-lower-three-fourths]}
         visible-annotations]

     (when (and (some? on-annotation-visible-lower-three-fourths)
                (pos? (count in-lower-three-fourths)))
       (doseq [annotation in-lower-three-fourths]
         (re-frame/dispatch
           (conj on-annotation-visible-lower-three-fourths annotation))))

     (when (and (some? on-annotation-visibility-change)
                (false? disable-visibility-change-dispatch))
       (re-frame/dispatch (into on-annotation-visibility-change
                                [options apis in-viewport])))

     (when (and (some? on-annotation-visible)
                (pos? (count in-viewport))
                (false? disable-visible-dispatch))
       (doseq [annotation in-viewport]
         (re-frame/dispatch (conj on-annotation-visible annotation))))

     (when (and (some? on-annotation-visible-upper-fourth)
                (pos? (count in-upper-fourth))
                (false? disable-upper-fourth-dispatch))
       (doseq [annotation in-upper-fourth]
         (re-frame/dispatch
          (conj on-annotation-visible-upper-fourth annotation))))

     (when (and (some? on-annotation-visible-upper-third)
                (pos? (count in-upper-third))
                (false? disable-upper-third-dispatch))
       (doseq [annotation in-upper-third]
         (re-frame/dispatch
          (conj on-annotation-visible-upper-third annotation))))

     (when (and (some? on-annotation-visible-middle-third)
                (pos? (count in-middle-third))
                (false? disable-middle-third-dispatch))
       (doseq [annotation in-middle-third]
         (re-frame/dispatch
          (conj on-annotation-visible-middle-third annotation))))

     (when (and (some? on-annotation-visible-lower-third)
                (pos? (count in-lower-third))
                (false? disable-lower-third-dispatch))
       (doseq [annotation in-lower-third]
         (re-frame/dispatch
          (conj on-annotation-visible-lower-third annotation)))))))

(defn- import-moment-annotations!
  [{:keys [annotation-manager moments user-annotations on-annotation-imported]}]
  (doseq [moment (into moments user-annotations)]
    (when-let [xfdf (or (:moment/xfdf moment)
                        (:xfdf moment))]
      (-> annotation-manager
          (j/call :importAnnotations xfdf)
          (j/call :then #(when (some? on-annotation-imported)
                           (re-frame/dispatch (conj on-annotation-imported %))))))))

(defn- set-annotation-display-author-map
  "At runtime, dynamically sets the displayed title depending on the given
  annotation"
  [options _ annotation]
  (if (= (gobj/get annotation "Author") (:username options))
    "Your Note"
    "Educator Note"))

(defn- get-initial-internal-state
  "Tracks custom state used by other events"
  []
  (atom {:document-loaded false
         :annotation-layer-loaded false
         :visible-pages #js []

         ;; Tracked for cleanup purposes
         :polling-interval-id nil
         :scroll-view-element nil
         :on-scroll-listener nil}))

(defn- pdftron-list->clj-list
  [raw-annotation-list]
  (let [annotation-list (atom [])]
    (js-invoke raw-annotation-list "forEach" #(swap! annotation-list conj %))
    @annotation-list))

(defn- prevent-on-delete
  [_options apis]
  (let [{:keys [tools]} apis
        keyDownEvent (gobj/getValueByKeys tools
                                          "AnnotationSelectTool"
                                          "prototype"
                                          "keyDown")]
    (gobj/set
     (gobj/getValueByKeys tools "AnnotationSelectTool" "prototype")
     "keyDown"
     (fn [e]
       (let [key-code (gobj/get e "keyCode")]
         (if (contains? #{8  ;; Backspace
                          46 ;; Delete
                          }
                        key-code)
           (js/console.log
            (str "TODO: For now we're just preventing the deletion of "
                 "an annotation, but we can prompt the user to "
                 "provide a better UX."))
           (keyDownEvent e)))))))

;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Listeners
;; ;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn- on-visible-pages-changed
  [{:keys [viewer-key]} {:keys [internal-state]} page-list]
  (re-frame/dispatch [:pdftron-viewer/update-visible-pages
                      {:viewer-key viewer-key
                       :visible-pages page-list}])
  (swap! internal-state assoc :visible-pages page-list))

(defn- on-annotation-loaded
  "Called after PDFTron has loaded the annotation layer"
  [{:keys [viewer-key] :as options} {:keys [internal-state] :as apis}]
  (swap! internal-state assoc :annotation-layer-loaded true)
  (re-frame/dispatch [:pdftron-viewer/update-annotations-loaded
                      {:viewer-key viewer-key}])
  (dispatch-visible-annotations options apis))

(defn- on-annotation-change
  "Called when an annotation has been added, updated, or removed from the
  doc-viewer"
  [{:keys [event-bus dispatch moments] :as options}
   {:keys [annotation-manager] :as apis}
   raw-annotation-list
   action
   context]
  (let [imported
        (or (gobj/get context "imported") false)

        {:keys [on-annotation-added
                on-annotation-removed
                on-annotation-updated]}
        event-bus

        moments-by-id
        (group-by :xfdf-id moments)]

    ;; Annotation change events can include multiple annotations. For example,
    ;; when a user highlights text across multiple pages it will break the
    ;; annotations up across pages.
    (go-loop [annotation-list (pdftron-list->clj-list raw-annotation-list)]
      (let [annotation (first annotation-list)
            next-list (rest annotation-list)
            moment (->> annotation
                        get-annotation-id
                        (get moments-by-id)
                        first)]

        ;; Change annotation bubble to hidden if hidden
        (when (:hidden-in-document moment)
          (gobj/set annotation "Hidden" true))

        ;; Trigger Core PDF Events

        (cond
          (and (= action "add")
               (false? imported))
          (do
            (dispatch-visible-annotations options apis)
            (dispatch
             [:pdftron-viewer/annotation-added options apis annotation])))

        ;; Trigger App Specific PDF Events
        (cond
          (and (= action "add")
               (false? imported)
               (some? on-annotation-added))
          (dispatch
           (into on-annotation-added
                 [options
                  (<! (annotation->xfdf annotation-manager annotation))
                  (get-annotation-id annotation)]))

          (and (= action "delete")
               (some? on-annotation-removed))
          (do
            (dispatch-visible-annotations options apis)
            (dispatch
             (into on-annotation-removed
                   [options (get-annotation-id annotation)])))

          (and (= action "modify")
               (some? on-annotation-updated))
          (do
            (dispatch-visible-annotations options apis)
            (dispatch
             (into on-annotation-updated
                   [options
                    (<! (annotation->xfdf annotation-manager annotation))
                    (get-annotation-id annotation)]))))

        (when (seq next-list)
          (recur next-list))))

    raw-annotation-list))

(defn- on-document-loaded
  "Called after PDFTron has loaded the document"
  [{:keys [moments user-annotations event-bus prevent-delete-shortcut]
    :as options
    :or {moments []
         user-annotations []}}
   {:keys [annotation-manager internal-state doc-viewer] :as apis}]
  (let [{:keys [on-document-initialized
                on-annotation-imported]} event-bus]
    (swap! internal-state merge {:document-loaded true
                                 :visible-pages (get-visible-pages doc-viewer)})

    (re-frame/dispatch [:pdftron-viewer/pdftron-initialized
                        {:options options
                         :apis apis
                         :on-document-initialized on-document-initialized}])

    ;; This setting we prevent annotations from being deleted using keyboard
    ;; shortcuts
    (when (true? prevent-delete-shortcut)
      (prevent-on-delete options apis))

    ;; Adds listerner to all annotation change events. Depending on the options
    ;; passed in on initialization, the behavior will vary.
    (js-invoke annotation-manager "on" "annotationChanged"
               (partial on-annotation-change options apis))

    (import-moment-annotations!
     {:on-annotation-imported on-annotation-imported
      :annotation-manager annotation-manager
      :moments moments
      :user-annotations user-annotations})))

(defn- on-document-unloaded
  "Called after PDFTron has unloaded the document"
  [_ _props]
  ;; {:keys [polling-interval-id scroll-view-element on-scroll-listener]} @(:internal-state _props)
  ;; TODO unload is not called, but when it is;
  ;; 1. Clear polling interval
  ;; 2. Remove scroll-view-element on scroll listener
  ;; 3. Remove on-page-resize listener
  ;; 4. Remove add-iframe-listeners
  )

(defn- on-page-number-updated
  [{:keys [event-bus] :as options} apis page-number-update]
  (let [{:keys [on-page-number-updated]} event-bus]
    (when (some? on-page-number-updated)
      (re-frame/dispatch (conj on-page-number-updated page-number-update)))
    ;; Reguarless of any custom event passed in, this event should still
    ;; dispatch to track analytics
    (re-frame/dispatch
     [:pdftron-viewer/page-number-updated options apis page-number-update])))

(defn- on-annotation-selected
  [{global-event-bus :event-bus :local/keys [event-bus dispatch]}
   _apis
   annotation-list
   action]
  (let [global-on-annotation-selected (:on-annotation-selected global-event-bus)
        local-on-annotation-selected (:on-annotation-selected event-bus)]

    (when (and (= action "selected")
               (some? annotation-list))
      (let [annotation (get-first-annotation annotation-list)]
        (when (some? global-on-annotation-selected)
          (re-frame/dispatch (conj global-on-annotation-selected annotation)))

        (when (and (some? local-on-annotation-selected)
                   (some? dispatch))
          (dispatch (conj local-on-annotation-selected annotation)))))
    
    (when (= action "deselected")
      (let [annotation (get-first-annotation annotation-list)]
        (when (and (some? local-on-annotation-selected)
                   (some? dispatch))
          (dispatch (conj local-on-annotation-selected annotation true)))))))

(defn- debounced-scroll*
  [options apis visible-annotations viewport-rect]
  (when-let [on-page-scroll* (get-in options [:event-bus :on-page-scroll])]
    (dispatch-visible-annotations options apis visible-annotations)
    (re-frame/dispatch (into on-page-scroll* [options apis viewport-rect])))
  ;; Reguarless of any custom event passed in, this event should still dispatch
  ;; to track analytics
  (re-frame/dispatch [:pdftron-viewer/page-scroll options apis viewport-rect]))

(def debounced-scroll (ku/debounce debounced-scroll* scroll-debounce-interval))

(defn- on-page-scroll [options apis _]
  (let [viewport-rect (get-viewport-rect apis)
        visible-annotations (get-visible-annotations apis viewport-rect)]
    (dispatch-visible-annotations
     (assoc options :disable-upper-fourth-dispatch true)
     apis
     visible-annotations)
    (debounced-scroll options apis visible-annotations viewport-rect)))

(defn- on-page-resize [options apis _]
  (dispatch-visible-annotations options apis))

(defn- on-tick
  [_options {:keys [internal-state]}]
  (when (and (:document-loaded @internal-state)
             (:annotation-layer-loaded @internal-state))
    ;; TODO Add any polling related logic here
    nil))

(defn- on-search
  [{:keys [viewer-key]} _apis search-term]
  (re-frame/dispatch [:pdftron-viewer/search-document
                      {:search-term search-term
                       :viewer-key viewer-key}]))

(defn- set-zoom-constraints!
  [pdf-api {:keys [max-zoom-level min-zoom-level]}]
  (js-invoke pdf-api "setMaxZoomLevel" max-zoom-level)
  (js-invoke pdf-api "setMinZoomLevel" min-zoom-level))

(defn- get-page-element*
  "Returns the page container element"
  [scroll-view-element page-number]
  (js-invoke scroll-view-element
             "querySelector"
             (str "#pageSection" page-number)))

(defn- add-iframe-listeners!
  [{:keys [event-bus viewer-key] :as options}
   {:keys [iframe-window doc-viewer] :as apis}]
  (let [{:keys [on-toggle-left-panel]} event-bus]
    (js-invoke iframe-window
               "addEventListener"
               "visibilityChanged"
               (fn [e]
                 (let [element (gobj/getValueByKeys e "detail" "element")
                       is-visible (gobj/getValueByKeys e "detail" "isVisible")]
                   (when (and (= element "leftPanel")
                              (some? on-toggle-left-panel))
                     (re-frame/dispatch
                      [:pdftron-viewer/on-toggle-left-panel
                       options apis {:is-visible is-visible
                                     :viewer-key viewer-key}])
                     ;; Trigger a scroll event on the doc viewer so that pdf
                     ;; guidelines are re-drawn
                     (-> ^js doc-viewer
                         (.getScrollViewElement)
                         (.dispatchEvent (js/Event. "scroll")))))))))

(defn- pdftron-initialized
  "Trigger after PDFTron lib has been initialized"
  [options pdf-api]
  (let [doc-viewer
        (gobj/get pdf-api "docViewer")

        scroll-view-element
        (js-invoke doc-viewer "getScrollViewElement")

        get-page-element
        (partial get-page-element* scroll-view-element)

        apis
        {:iframe-window
         (gobj/get pdf-api "iframeWindow")

         :internal-state
         (get-initial-internal-state)

         :pdf-api
         pdf-api

         :doc-viewer
         doc-viewer

         :annotation-manager
         (gobj/get pdf-api "annotManager")

         :disable-elements
         (gobj/get pdf-api "disableElements")

         :enable-elements
         (gobj/get pdf-api "enableElements")

         :open-elements
         (gobj/get pdf-api "openElements")

         :close-elements
         (gobj/get pdf-api "closeElements")

         :set-header-items
         (gobj/get pdf-api "setHeaderItems")

         :enable-tools
         (gobj/get pdf-api "enableTools")

         :disable-tools
         (gobj/get pdf-api "disableTools")

         :unregister-tool
         (gobj/get pdf-api "unregisterTool")

         :register-tool
         (gobj/get pdf-api "registerTool")

         :enable-features
         (gobj/get pdf-api "enableFeatures")

         :disable-features
         (gobj/get pdf-api "disableFeatures")

         :element-open?
         (gobj/get pdf-api "isElementOpen")

         :annotations
         (gobj/get pdf-api "Annotations")

         :hotkeys
         (gobj/get pdf-api "hotkeys")

         :feature
         (gobj/get pdf-api "Feature")

         :tools
         (gobj/get pdf-api "Tools")

         :core-controls
         (gobj/get pdf-api "CoreControls")

         :get-page-element
         get-page-element}

        polling-interval-id
        (js-invoke js/window "setInterval"
                   (partial on-tick options apis)
                   poll-document-interval)

        on-scroll-listener
        (partial on-page-scroll options apis)

        on-page-resize-listener
        (partial on-page-resize options apis)]

    ;; On initial load, page-change triggers before loading the document
    ;; This means that an analytic event is fired without an ecid. This
    ;; code ensures we will have access to the ecid even if it's called
    ;; out of order.
    ;;
    ;; TODO: Figure out a better way to solve this
    (swap! pdf-viewer-instances
           update
           (:viewer-key options)
           merge
           (select-keys options [:ecid :viewer-key]))

    (swap! (:internal-state apis)
           merge
           {:scroll-view-element scroll-view-element
            :on-scroll-listener on-scroll-listener
            :on-page-resize-listener on-page-resize-listener
            :polling-interval-id polling-interval-id})

    (set-zoom-constraints! pdf-api options)

    (js-invoke js/window
               "addEventListener" "resize" on-page-resize-listener)

    (js-invoke scroll-view-element
               "addEventListener" "scroll" on-scroll-listener)

    (toolbar/set-default-header-tools options apis)

    (hotkeys/set-hotkeys! options apis)

    (add-iframe-listeners! options apis)

    ;; Annotation Manager Listeners

    (js-invoke (:annotation-manager apis) "setAnnotationDisplayAuthorMap"
               (partial set-annotation-display-author-map options apis))

    (js-invoke (:annotation-manager apis) "on" "annotationSelected"
               (partial on-annotation-selected options apis))

    ;; Document Viewer Listeners

    (js-invoke (:doc-viewer apis) "on" "documentLoaded"
               (partial on-document-loaded options apis))

    (js-invoke (:doc-viewer apis) "on" "annotationsLoaded"
               (partial on-annotation-loaded options apis))

    (js-invoke (:doc-viewer apis) "on" "documentUnloaded"
               (partial on-document-unloaded options apis))

    (js-invoke (:doc-viewer apis) "on" "pageNumberUpdated"
               (partial on-page-number-updated options apis))

    (js-invoke (:doc-viewer apis) "on" "visiblePagesChanged"
               (partial on-visible-pages-changed options apis))

    (set-tool-styles! options apis)

    ;; Seach API Listener
    (js-invoke pdf-api "addSearchListener"
               (partial on-search options apis))))

;; Public API

(defn init-web-viewer
  [{:keys [url
           container
           is-admin
           is-learner
           is-view-only
           username
           max-zoom-level
           min-zoom-level
           pdftron-js-path
           pdftron-css-path]
    :as options
    :or {pdftron-js-path "/vendor/pdftron/lib/"
         pdftron-css-path "/vendor/css/custom_pdf_tron.css"}}]
  (let [fmt-options
        (assoc options
               :max-zoom-level
               (or max-zoom-level c/default-max-zoom-level)

               :min-zoom-level
               (or min-zoom-level c/default-min-zoom-level))]
    (-> (PDFTronWebViewer
         (clj->js
          (cond->
           {:annotationUser username
            :path pdftron-js-path
            :initialDoc url
            :backendType "ems"
            :css pdftron-css-path
            :disabledElements (cond is-learner
                                    invalid-learner-pdftron-elements

                                    is-admin
                                    invalid-admin-pdftron-elements

                                    is-view-only
                                    invalid-view-only-pdftron-elements)}
            (seq pdftron-api-key)
            (assoc :licenseKey pdftron-api-key)

            (true? is-admin)
            (assoc :isAdminUser true)))
         container)
        (js-invoke "then" (partial pdftron-initialized fmt-options))
        (js-invoke "catch" js/console.error))))
